import { format, parseISO, subDays } from 'date-fns';
import { AdminTimeRangePicker } from '@/components/AdminTimeRangePicker';
import { TimeRangeFilter } from '@/components/TimeRangeFilter';
import {
  formatTimeRangeDetail,
  getAdminTimeRangeOptions,
} from '@/lib/timeRangeOptions';
import { financeCountryOptions } from '@/features/finance/lib/financeColors';

export type DashboardRangeValue = string;

export function getDashboardRangeOptions() {
  return getAdminTimeRangeOptions();
}

/** @deprecated Prefer getDashboardRangeOptions(). */
export const DASHBOARD_RANGE_OPTIONS = getAdminTimeRangeOptions();

export function formatDashboardRangeDetail(startDate?: string, endDate?: string): string {
  return formatTimeRangeDetail(startDate, endDate);
}

/**
 * Prior equal-length window label for KPI footers (Figma: “vs May 13 – May 19”).
 * Current overview reports half-open [startDate, endDate); prior is the same duration
 * ending at startDate: [priorStart, startDate). Display uses inclusive calendar ends,
 * so the visible prior end is the day before startDate.
 */
export function formatPriorComparisonLabel(startDate?: string, endDate?: string): string | null {
  if (!startDate || !endDate) return null;
  try {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const durationMs = end.getTime() - start.getTime();
    if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
    const priorStart = new Date(start.getTime() - durationMs);
    const priorEndInclusive = subDays(start, 1);
    // Figma short form: "May 13 – May 19" (no year when same calendar year as prior end).
    const sameYear = priorStart.getFullYear() === priorEndInclusive.getFullYear();
    const fmt = sameYear ? 'MMM d' : 'MMM d, yyyy';
    return `${format(priorStart, fmt)} – ${format(priorEndInclusive, fmt)}`;
  } catch {
    return null;
  }
}

/**
 * TM-3570: All-time KPI Δ% still compares the trailing 30-day growth window, even though
 * overview startDate is earliest app activity. Label the prior 30 days before that window.
 */
export function formatAllTimePriorComparisonLabel(
  endDate?: string,
  deltaDays = 30,
): string | null {
  if (!endDate || deltaDays < 1) return null;
  try {
    const end = parseISO(endDate);
    if (!Number.isFinite(end.getTime())) return null;
    const start = subDays(end, deltaDays);
    return formatPriorComparisonLabel(start.toISOString(), endDate);
  } catch {
    return null;
  }
}

/**
 * Compact date range + country filter row for the Dashboard page header and
 * Platform Growth Overview chart. Same `users.country_code` model as Finance
 * (Stripe Connect country / UNASSIGNED).
 */
export function DashboardChartFilters({
  range,
  onRangeChange,
  startDate,
  endDate,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  dateAriaLabel = 'Change date range',
  countryAriaLabel = 'Change country filter',
}: {
  range: DashboardRangeValue;
  onRangeChange: (value: DashboardRangeValue) => void;
  startDate?: string;
  endDate?: string;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  dateAriaLabel?: string;
  countryAriaLabel?: string;
}) {
  const countryOptions = financeCountryOptions(availableCountryCodes).map((opt) => {
    if (opt.value === '') {
      return {
        value: '',
        label: 'All countries',
        detail: 'All States',
      };
    }
    if (opt.value === 'UNASSIGNED') {
      return {
        value: 'UNASSIGNED',
        // Dashboard-only copy. Finance keeps financeCountryLabel (TM-3572).
        label: 'Others',
        detail: opt.detail,
      };
    }
    return {
      value: opt.value,
      label: opt.label,
      detail: 'All States',
    };
  });
  const countryOption = countryOptions.find((o) => o.value === countryCode) ?? countryOptions[0]!;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <AdminTimeRangePicker
        preset="admin"
        value={range}
        onChange={(v) => onRangeChange(v as DashboardRangeValue)}
        {...(startDate ? { startDate } : {})}
        {...(endDate ? { endDate } : {})}
        ariaLabel={dateAriaLabel}
        className="w-full min-w-[200px] sm:w-[260px]"
      />
      <TimeRangeFilter
        size="compact"
        label={
          countryOption.value
            ? `${countryOption.label} — ${countryOption.detail}`
            : countryOption.label
        }
        detail="Audience country"
        value={countryCode}
        options={countryOptions.map((o) => ({ value: o.value, label: o.label }))}
        onChange={onCountryCodeChange}
        ariaLabel={countryAriaLabel}
        className="w-full min-w-[200px] sm:w-[280px]"
      />
    </div>
  );
}

/** @deprecated Prefer DashboardChartFilters — alias kept for existing imports. */
export const DashboardGrowthFilters = DashboardChartFilters;
